import React from 'react';
import {Box, Button, Chip, Grid, Paper, Typography,} from '@mui/material';
import type {Application, DeploymentStatus, Node, NodeType, Project} from '../types';
import {calculateProjectPrice, DeploymentStage, formatPrice, timePriceToMonthlyPrice, NodeRole, formatDateTime} from '../types';

interface ProjectViewProps {
  project: Project;
  controlNodes: Record<string, number>;
  workerNodes: Record<string, number>;
  status: DeploymentStatus;
  nodes: Node[];
  nodeTypes: NodeType[];
  applications: Application[];
}

const downloadFile = (content: string, filename: string) => {
  const blob = new Blob([content], {type: 'text/plain'});
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const ProjectView: React.FC<ProjectViewProps> = ({
                                                          project,
                                                          controlNodes,
                                                          workerNodes,
                                                          status,
                                                          nodes,
                                                          nodeTypes,
                                                          applications,
                                                        }) => {
  const controlNodesCount = Object.values(controlNodes).reduce(
    (sum, count) => sum + count,
    0
  );
  const workerNodesCount = Object.values(workerNodes).reduce(
    (sum, count) => sum + count,
    0
  );

  const activeControlNodes = nodes.filter(n => n.role === NodeRole.CONTROL_NODE && !n.deletedAt);
  const activeWorkerNodes = nodes.filter(n => n.role === NodeRole.WORKER_NODE && !n.deletedAt);

  const totalPrice = calculateProjectPrice(project, controlNodes, workerNodes, nodeTypes, applications);

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        {project.name}
      </Typography>

      {/* Status */}
      <Paper sx={{p: 3, mb: 3}}>
        <Typography variant="h6" gutterBottom>
          Status
        </Typography>
        <Chip
          label={status.stage.replace(/_/g, ' ')}
          color={
            status.stage === DeploymentStage.DONE
              ? 'success'
              : status.stage === DeploymentStage.FAILED
                ? 'error'
                : 'primary'
          }
        />
      </Paper>

      {/* Control Nodes */}
      {controlNodesCount > 0 && (
        <Paper sx={{p: 3, mb: 3}}>
          <Typography variant="h6" gutterBottom>
            Control Nodes ({controlNodesCount})
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(controlNodes).map(([nodeTypeId, count]) => {
              const nodeType = nodeTypes.find((nt) => nt.id === nodeTypeId);
              if (!nodeType) return null;
              return (
                <Grid item key={nodeTypeId} xs={12} sm={6} md={4}>
                  <Box>
                    <Typography variant="subtitle1">
                      {count}x {nodeType.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {nodeType.cpu} CPU, {nodeType.ram} GB RAM
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {formatPrice(timePriceToMonthlyPrice(nodeType.price))}/month each
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}

      {/* Worker Nodes */}
      {workerNodesCount > 0 && (
        <Paper sx={{p: 3, mb: 3}}>
          <Typography variant="h6" gutterBottom>
            Worker Nodes ({workerNodesCount})
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(workerNodes).map(([nodeTypeId, count]) => {
              const nodeType = nodeTypes.find((nt) => nt.id === nodeTypeId);
              if (!nodeType) return null;
              return (
                <Grid item key={nodeTypeId} xs={12} sm={6} md={4}>
                  <Box>
                    <Typography variant="subtitle1">
                      {count}x {nodeType.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {nodeType.cpu} CPU, {nodeType.ram} GB RAM
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {formatPrice(timePriceToMonthlyPrice(nodeType.price))}/month each
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}

      {/* Applications */}
      {project.applications.length > 0 && (
        <Paper sx={{p: 3, mb: 3}}>
          <Typography variant="h6" gutterBottom>
            Applications ({project.applications.length})
          </Typography>
          <Grid container spacing={2}>
            {project.applications.map((appId) => {
              const app = applications.find((a) => a.id === appId);
              if (!app) return null;
              return (
                <Grid item key={appId} xs={12} sm={6} md={4}>
                  <Box>
                    <Typography variant="subtitle1">{app.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {app.description}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {formatPrice(timePriceToMonthlyPrice(app.price))}/month
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}

      {/* Total Cost */}
      <Paper sx={{p: 2}}>
        <Typography variant="body1" align="center">
          Total Monthly Cost: {formatPrice(totalPrice)}
        </Typography>
      </Paper>

      {/* Provisioned Nodes */}
      {status.stage === DeploymentStage.DONE && (activeControlNodes.length > 0 || activeWorkerNodes.length > 0) && (
        <Paper sx={{p: 3, mt: 3}}>
          <Typography variant="h6" gutterBottom>
            Provisioned Hosts
          </Typography>

          {activeControlNodes.length > 0 && (
            <Box sx={{mb: 3}}>
              <Typography variant="subtitle1" gutterBottom>
                Control Nodes
              </Typography>
              {activeControlNodes.map((node) => {
                const nodeType = nodeTypes.find((nt) => nt.id === node.type);
                return (
                  <Box key={node.id} sx={{mb: 1}}>
                    <Typography variant="body2">
                      <strong>{node.ip || 'N/A'}</strong> - {nodeType?.name || node.type}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      User: {node.user || 'N/A'} | Created: {formatDateTime(node.createdAt)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}

          {activeWorkerNodes.length > 0 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Worker Nodes
              </Typography>
              {activeWorkerNodes.map((node) => {
                const nodeType = nodeTypes.find((nt) => nt.id === node.type);
                return (
                  <Box key={node.id} sx={{mb: 1}}>
                    <Typography variant="body2">
                      <strong>{node.ip || 'N/A'}</strong> - {nodeType?.name || node.type}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      User: {node.user || 'N/A'} | Created: {formatDateTime(node.createdAt)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>
      )}

      {/* Connection Information */}
      {status.stage === DeploymentStage.DONE && (status.kubeconfig || status.sshKey) && (
        <Paper sx={{p: 3, mt: 3}}>
          <Typography variant="h6" gutterBottom>
            Access Credentials
          </Typography>
          <Typography variant="body2" color="warning.main" sx={{mb: 2}}>
            ⚠️ These are secret login credentials. Keep them secure and do not share them.
          </Typography>
          <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
            {status.kubeconfig && (
              <Button
                variant="contained"
                onClick={() => downloadFile(status.kubeconfig!, 'kubeconfig')}
              >
                Download Kubeconfig
              </Button>
            )}
            {status.sshKey && (
              <Button
                variant="contained"
                onClick={() => downloadFile(status.sshKey!, 'id_ed25519')}
              >
                Download SSH Key
              </Button>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};
